// Re-export shared types and helpers
export type { WordTiming, KenBurnsConfig, Scene, LogoSceneConfig } from '../shared/types';
export { msToFrames, getCurrentScene, getVisibleWords, KEN_BURNS_PRESETS } from '../shared/types';
import type { WordTiming, Scene } from '../shared/types';
import { KEN_BURNS_PRESETS } from '../shared/types';

// Color palettes — A Rock Asphalt Social Proof theme
export const SCENE_COLORS = {
  scene1: { primaryColor: '#F59E0B', accentColor: '#ffffff' },   // Amber/White - A Rock Asphalt
  scene2: { primaryColor: '#ffffff', accentColor: '#EF4444' },   // White/Red - Quiet phone
  scene3: { primaryColor: '#8B5CF6', accentColor: '#ffffff' },   // Purple/White - Exclusive
  scene4: { primaryColor: '#22C55E', accentColor: '#FCD34D' },   // Green/Gold - Growth
  scene5: { primaryColor: '#3B82F6', accentColor: '#ffffff' },   // Blue/White - Booked
  scene6: { primaryColor: '#FCD34D', accentColor: '#ffffff' },   // Gold/White - CTA
  logo: { primaryColor: '#ffffff', accentColor: '#FCD34D' },
};

export const SCENE_IMAGES = {
  scene1: 'images/pvm-3800-to-12000/scene1-arock.png',
  scene2: 'images/pvm-3800-to-12000/scene2-quiet.png',
  scene3: 'images/pvm-3800-to-12000/scene3-exclusive.png',
  scene4: 'images/pvm-3800-to-12000/scene4-growth.png',
  scene5: 'images/pvm-3800-to-12000/scene5-booked.png',
  scene6: 'images/pvm-3800-to-12000/scene6-report.png',
};

export const REPORT_SLIDES = [
  'images/pvm-quiet-phone/report-slide-01.png',
  'images/pvm-quiet-phone/report-slide-05.png',
  'images/pvm-quiet-phone/report-slide-07.png',
  'images/pvm-quiet-phone/report-slide-12.png',
];

export const DRAMATIC_WORDS = ['$3,800', '$12,000', 'exclusive', 'Now?', 'Free'];

// Word timings from Whisper — manual corrections: "A"+"-Rock"→"A Rock", "$3"+",800"→"$3,800", "$12"+",000"→"$12,000", "Now,"→"Now?", "market"→"Market", "report."→"Report.", "10"→"Ten"
export const WORD_TIMINGS: WordTiming[] = [
  // Scene 1: A Rock Asphalt intro
  { text: 'A Rock', startMs: 0, endMs: 320 },
  { text: 'Asphalt', startMs: 320, endMs: 800 },
  { text: 'was', startMs: 800, endMs: 960 },
  { text: 'averaging', startMs: 960, endMs: 1300 },
  { text: '$3,800', startMs: 1300, endMs: 2020 },
  { text: 'per', startMs: 2020, endMs: 2300 },
  { text: 'job.', startMs: 2300, endMs: 2920 },

  // Scene 2: Quiet phone
  { text: 'Good', startMs: 2920, endMs: 4000 },
  { text: 'work.', startMs: 4000, endMs: 4460 },
  { text: 'Solid', startMs: 4460, endMs: 5180 },
  { text: 'crew.', startMs: 5180, endMs: 5760 },
  { text: 'But', startMs: 5760, endMs: 6500 },
  { text: 'the', startMs: 6500, endMs: 6620 },
  { text: 'phone', startMs: 6620, endMs: 6800 },
  { text: "wasn't", startMs: 6800, endMs: 7140 },
  { text: 'ringing', startMs: 7140, endMs: 7280 },
  { text: 'enough.', startMs: 7280, endMs: 7780 },

  // Scene 3: Exclusive leads
  { text: 'We', startMs: 7780, endMs: 8360 },
  { text: 'built', startMs: 8360, endMs: 8620 },
  { text: 'them', startMs: 8620, endMs: 8780 },
  { text: 'exclusive', startMs: 8780, endMs: 9100 },
  { text: 'leads.', startMs: 9100, endMs: 9800 },
  { text: 'No', startMs: 9800, endMs: 10340 },
  { text: 'sharing.', startMs: 10340, endMs: 10940 },
  { text: 'No', startMs: 10940, endMs: 11560 },
  { text: 'bidding.', startMs: 11560, endMs: 12020 },
  { text: 'Just', startMs: 12020, endMs: 12780 },
  { text: 'homeowners', startMs: 12780, endMs: 13180 },
  { text: 'calling', startMs: 13180, endMs: 13580 },
  { text: 'them', startMs: 13580, endMs: 13820 },
  { text: 'directly.', startMs: 13820, endMs: 14560 },

  // Scene 4: Growth / Results
  { text: 'Now?', startMs: 14560, endMs: 15300 },
  { text: '$12,000', startMs: 15300, endMs: 16040 },
  { text: 'per', startMs: 16040, endMs: 16160 },
  { text: 'job.', startMs: 16160, endMs: 16700 },

  // Scene 5: Same company
  { text: 'Same', startMs: 16700, endMs: 17320 },
  { text: 'company.', startMs: 17320, endMs: 17840 },
  { text: 'Different', startMs: 17840, endMs: 18500 },
  { text: 'marketing.', startMs: 18500, endMs: 19100 },

  // Scene 6: CTA
  { text: 'Free', startMs: 19100, endMs: 19760 },
  { text: 'Market', startMs: 19760, endMs: 20020 },
  { text: 'Report.', startMs: 20020, endMs: 20780 },
  { text: 'Ten', startMs: 20780, endMs: 21320 },
  { text: 'minutes.', startMs: 21320, endMs: 21760 },
  { text: 'Link', startMs: 21760, endMs: 22060 },
  { text: 'below.', startMs: 22060, endMs: 22480 },
];

export const SCENES: Scene[] = [
  {
    id: 1,
    name: 'A Rock Asphalt',
    startMs: 0,
    endMs: 2920,
    words: WORD_TIMINGS.slice(0, 7),
    ...SCENE_COLORS.scene1,
    backgroundImage: SCENE_IMAGES.scene1,
    kenBurns: KEN_BURNS_PRESETS.zoomIn,
  },
  {
    id: 2,
    name: 'Quiet Phone',
    startMs: 2920,
    endMs: 7780,
    words: WORD_TIMINGS.slice(7, 17),
    ...SCENE_COLORS.scene2,
    backgroundImage: SCENE_IMAGES.scene2,
    kenBurns: KEN_BURNS_PRESETS.panRight,
  },
  {
    id: 3,
    name: 'Exclusive',
    startMs: 7780,
    endMs: 14560,
    words: WORD_TIMINGS.slice(17, 31),
    ...SCENE_COLORS.scene3,
    backgroundImage: SCENE_IMAGES.scene3,
    kenBurns: KEN_BURNS_PRESETS.dramaticZoom,
  },
  {
    id: 4,
    name: 'Growth',
    startMs: 14560,
    endMs: 16700,
    words: WORD_TIMINGS.slice(31, 35),
    ...SCENE_COLORS.scene4,
    backgroundImage: SCENE_IMAGES.scene4,
    kenBurns: KEN_BURNS_PRESETS.zoomOut,
  },
  {
    id: 5,
    name: 'Comparison',
    startMs: 16700,
    endMs: 19100,
    words: WORD_TIMINGS.slice(35, 39),
    ...SCENE_COLORS.scene5,
    backgroundImage: SCENE_IMAGES.scene5,
    kenBurns: KEN_BURNS_PRESETS.panLeft,
  },
  {
    id: 6,
    name: 'CTA',
    startMs: 19100,
    endMs: 22480,
    words: WORD_TIMINGS.slice(39),
    ...SCENE_COLORS.scene6,
    backgroundImage: SCENE_IMAGES.scene6,
    kenBurns: KEN_BURNS_PRESETS.zoomWithTilt,
  },
];

export const LOGO_SCENE = {
  startMs: 22480,
  endMs: 27580,
  ...SCENE_COLORS.logo,
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
